import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { RiMailSendLine, RiSmartphoneLine } from '@/shared/icons/remix'
import { palettePrimaryMain } from '@/theme/designTokens'
import type { SpChannelAvailability, SpOnboardingChannel } from '../lib/types'
import {
  channelCardBaseSx,
  channelCardDisabledSx,
  channelCardLabelSx,
  channelCardLabelSelectedSx,
  channelCardSelectedSx,
  channelCheckmarkSx,
  channelGridSx,
} from '../styles/instrumentOnboardingStyles'
import {
  SP_SIGNUP_YELLOW,
  signupShareChannelCheckmarkSx,
  signupShareChannelLabelSelectedSx,
  signupShareChannelSelectedSx,
  useSignupShareTheme,
} from '../styles/signupShareTheme'

interface VerificationMethodCardsProps {
  readonly channelAvailability: SpChannelAvailability
  readonly selected: SpOnboardingChannel
  readonly onSelect: (channel: SpOnboardingChannel) => void
  readonly disabled?: boolean
}

function ChannelCard({
  channel,
  label,
  icon,
  selected,
  onSelect,
  disabled,
  unavailable,
}: Readonly<{
  channel: SpOnboardingChannel
  label: string
  icon: ReactNode
  selected: boolean
  onSelect: (channel: SpOnboardingChannel) => void
  disabled?: boolean
  unavailable?: boolean
}>) {
  const signupTheme = useSignupShareTheme()
  const isDisabled = Boolean(disabled || unavailable)
  const selectedAccent = selected && !isDisabled
  let iconColor = '#0b1c30'
  if (selectedAccent) {
    iconColor = signupTheme ? SP_SIGNUP_YELLOW : palettePrimaryMain
  }

  return (
    <Box
      component="button"
      type="button"
      role="radio"
      aria-checked={selected && !isDisabled}
      aria-disabled={isDisabled}
      disabled={isDisabled}
      data-testid={`instrument-onboarding-channel-${channel}`}
      onClick={() => {
        if (!isDisabled) onSelect(channel)
      }}
      sx={[
        channelCardBaseSx,
        selectedAccent ? channelCardSelectedSx : null,
        selectedAccent && signupTheme ? signupShareChannelSelectedSx : null,
        isDisabled ? channelCardDisabledSx : null,
      ]}
    >
      {selectedAccent && (
        <Box
          sx={[channelCheckmarkSx, signupTheme ? signupShareChannelCheckmarkSx : null]}
          aria-hidden
        >
          ✓
        </Box>
      )}
      <Box sx={{ color: iconColor }} aria-hidden>
        {icon}
      </Box>
      <Typography
        component="span"
        sx={[
          channelCardLabelSx,
          selectedAccent ? channelCardLabelSelectedSx : null,
          selectedAccent && signupTheme ? signupShareChannelLabelSelectedSx : null,
        ]}
      >
        {label}
      </Typography>
      {unavailable && (
        <Typography
          component="span"
          sx={{ fontSize: '12px', lineHeight: '14px', color: '#6b7a8c', m: 0 }}
        >
          Not on file
        </Typography>
      )}
    </Box>
  )
}

export function VerificationMethodCards({
  channelAvailability,
  selected,
  onSelect,
  disabled = false,
}: VerificationMethodCardsProps) {
  return (
    <Box
      role="radiogroup"
      aria-label="Choose verification method"
      data-testid="instrument-onboarding-channel-group"
      sx={channelGridSx}
    >
      <ChannelCard
        channel="sms"
        label="SMS"
        icon={<RiSmartphoneLine size={24} />}
        selected={selected === 'sms'}
        onSelect={onSelect}
        disabled={disabled}
        unavailable={!channelAvailability.sms}
      />
      <ChannelCard
        channel="email"
        label="Email"
        icon={<RiMailSendLine size={24} />}
        selected={selected === 'email'}
        onSelect={onSelect}
        disabled={disabled}
        unavailable={!channelAvailability.email}
      />
    </Box>
  )
}
